/**
 * Choosing between the light and dark themes.
 *
 * Dark is the default. A player can switch to light (and back) with the theme
 * button next to mute, and the choice is remembered between visits.
 */
import type { ThemeName } from "./palette";

/** The theme used until the player picks one. */
export const DEFAULT_THEME: ThemeName = "dark";

/** Where the player's choice is saved (shared by the game and the palette page). */
const STORAGE_KEY = "rings:theme";

/**
 * The saved theme, or the default (dark) if nothing valid is saved. Anything
 * else stored under the key, such as "system" from an older version of the
 * game, also falls back to the default.
 */
export function loadTheme(): ThemeName {
	try {
		const saved = localStorage.getItem(STORAGE_KEY);
		if (saved === "light" || saved === "dark") return saved;
	} catch {
		// Storage can be unavailable (some private browsing modes); fall through.
	}
	return DEFAULT_THEME;
}

/** Remember a theme choice. Failures are ignored: remembering is a convenience. */
export function saveTheme(theme: ThemeName): void {
	try {
		localStorage.setItem(STORAGE_KEY, theme);
	} catch {
		// Ignore.
	}
}

/** The other theme: light for dark, dark for light. */
export function otherTheme(theme: ThemeName): ThemeName {
	return theme === "dark" ? "light" : "dark";
}

/**
 * Tell the page's CSS which theme is in use. The stylesheet (src/theme.css)
 * chooses its colors from the `data-theme` attribute on the `<html>` element,
 * and `color-scheme` makes built-in controls (scrollbars, inputs) match.
 */
export function applyThemeToPage(theme: ThemeName): void {
	document.documentElement.dataset.theme = theme;
	document.documentElement.style.colorScheme = theme;
}
